import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient, { setAuthToken, getAuthToken } from '../utils/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // ── Cross-tab session sync ─────────────────────────────────────────────
  // localStorage fires a 'storage' event in OTHER tabs when the auth token
  // changes. We use it to:
  //   • Sign out this tab immediately if the user logs out in another tab
  //     (prevents the "still logged in over there" ghost).
  //   • Re-check auth if a new token appears (another tab just logged in
  //     with the same account — this tab should adopt that session).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'access_token') return;
      if (e.newValue == null && e.oldValue) {
        // Logged out in another tab.
        setAuthToken(null);
        setUser(false);
        setImpersonating(false);
      } else if (e.newValue && e.newValue !== e.oldValue) {
        // New login in another tab → adopt it here.
        checkAuth();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Session-superseded detector ────────────────────────────────────────
  // Any API call that returns 401 with detail='session_superseded' means
  // this user logged in on another device and chose to end this session.
  // Surface a clear toast instead of a generic "not authenticated" error,
  // and clean up local state so they land on /login cleanly.
  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 401 && detail === 'session_superseded') {
          setAuthToken(null);
          setUser(false);
          setImpersonating(false);
          toast.error('You were signed out because this account logged in on another device.');
          // Redirect to /login unless we're already there.
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.replace('/login');
          }
        }
        return Promise.reject(err);
      }
    );
    return () => apiClient.interceptors.response.eject(interceptorId);
  }, []);

  const checkAuth = async () => {
    // If we have no cookie AND no token, skip the call — it will just 401.
    const hasToken = !!getAuthToken();
    try {
      const { data } = await apiClient.get('/auth/me');
      setUser(data);
    } catch (error) {
      if (!hasToken) setAuthToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, isAdmin = false, forceSession = false) => {
    try {
      const endpoint = isAdmin ? '/admin/login' : '/tenant/login';
      const body = { email, password };
      if (forceSession) body.force_session = true;
      const { data } = await apiClient.post(endpoint, body);

      // Store the JWT for Bearer-header fallback (belt + suspenders alongside
      // HttpOnly cookies). Critical for mobile Safari / private-mode browsers.
      if (data.access_token) {
        setAuthToken(data.access_token);
      }

      setUser(data);
      setImpersonating(false);
      return { success: true, role: data.role, store_id: data.store_id };
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (!e.response) {
        const offline = typeof navigator !== "undefined" && navigator.onLine === false;
        return {
          success: false,
          networkError: true,
          error: offline
            ? "You appear to be offline. Please check your connection and try again."
            : "Can't reach the server. Please try again in a moment or contact support at admin@musicsync.net.",
        };
      }
      // 409 = another active session for this account. Frontend shows a
      // confirm dialog, and re-calls login(..., forceSession=true).
      if (status === 409 && detail === 'active_session_exists') {
        return { success: false, activeSessionExists: true };
      }
      if (status === 401) {
        return { success: false, error: "Invalid email or password. Please check your credentials and try again." };
      }
      if (status === 403) {
        const msg = formatApiErrorDetail(detail);
        const blocked = (msg || "").toLowerCase().includes("subscription");
        return { success: false, blocked, error: msg || "Access denied." };
      }
      if (status >= 500) {
        return { success: false, error: "Server error. Please try again shortly or contact admin@musicsync.net." };
      }
      return { success: false, error: formatApiErrorDetail(detail) || e.message || "Login failed. Please try again." };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAuthToken(null);
      setUser(false);
      setImpersonating(false);
    }
  };

  const impersonate = async (tenantId) => {
    try {
      const { data } = await apiClient.post(`/admin/impersonate/${tenantId}`, {});
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data);
      setImpersonating(true);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const exitImpersonation = async () => {
    try {
      const { data } = await apiClient.post('/admin/exit-impersonation', {});
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data);
      setImpersonating(false);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, impersonating, impersonate, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
